import { API } from "@apis/index";
import { BASE_PATH_RUNTIME, KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_FRONTEND_CLIENT_ID } from "@configs/runtimeConfig";
import { LOCAL_STORAGE, COOKIES, LOGGED_IN_STATUS } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { createSessionCookie, getCookieValue } from "@utils/common";
import { clearAuthSession, resetCachedUserData } from "@utils/authSession";
import { setSessionBootstrapError } from "@utils/sessionBootstrapError";
import i18n from "i18next";
import Keycloak from "keycloak-js";
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import { KeycloakAuthContext } from "./keycloakAuthContext";
import { isLogoutInProgress, setKeycloakInstance } from "./keycloakClient";

export { useKeycloakAuth } from "./keycloakAuthContext";

function appBasePath() {
  const trimmed = (BASE_PATH_RUNTIME || "/").replace(/\/+$/, "");
  return trimmed === "/" ? "" : trimmed;
}

function buildSilentCheckSsoUrl() {
  const base = appBasePath();
  return `${window.location.origin}${base}/silent-check-sso.html`.replace(/([^:]\/)\/+/g, "$1");
}

function loginPageRedirectUri() {
  const base = appBasePath();
  return `${window.location.origin}${base}/login`.replace(/([^:]\/)\/+/g, "$1");
}

const KEYCLOAK_UI_LOCALES = new Set(["en", "ko", "ja"]);

function normalizeKeycloakLocale(locale) {
  const normalized = locale?.toLowerCase();
  return KEYCLOAK_UI_LOCALES.has(normalized) ? normalized : "ko";
}

async function resolveKeycloakLocale() {
  const saved = localStorage.getItem(LOCAL_STORAGE.LANGUAGE);
  if (saved) {
    return normalizeKeycloakLocale(saved);
  }

  try {
    const res = await API.get("locale_default", {
      skipAuth: true,
      skipAuthRefresh: true,
      skipErrorToast: true,
    });
    const locale = res?.data?.locale;
    if (locale) {
      return normalizeKeycloakLocale(locale);
    }
  } catch (err) {
    console.warn("Failed to fetch default locale for Keycloak login", err);
  }

  return normalizeKeycloakLocale(i18n.language);
}

function buildKeycloakLoginUrl(kc, { redirectUri, locale }) {
  const loginUrl = kc.createLoginUrl({ redirectUri, locale });
  const url = new URL(loginUrl, window.location.origin);
  url.searchParams.set("kc_locale", locale);
  return url.toString();
}

async function clearKeycloakSessionAfterBootstrapFailure(kc, err) {
  setSessionBootstrapError(err);
  await clearAuthSession({ clearIndexedDb: true });
  if (kc) {
    await kc.logout({ redirectUri: loginPageRedirectUri() });
  }
}

export function KeycloakAuthProvider({ children }) {
  const { updatePermissions, updateLocaleDefault } = useContext(PermissionContext);
  const clientConfigContext = useClientConfig();
  const fetchClientConfig = clientConfigContext?.fetchClientConfig ?? (async () => {});

  const [ready, setReady] = useState(false);
  const [keycloak, setKeycloak] = useState(null);
  const loginRedirectInProgressRef = useRef(false);
  const sessionBootstrapInProgressRef = useRef(false);

  const hasAppSession = useCallback(() => {
    const loginStatus = getCookieValue(COOKIES.LOGIN_STATUS);
    const userId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    return Boolean(loginStatus === LOGGED_IN_STATUS && userId && keycloak?.authenticated);
  }, [keycloak]);

  const applySessionPayload = useCallback(
    async (kc) => {
      const res = await API.get("auth/session", { skipAuthRefresh: true });
      const data = res?.data?.data;
      if (!data) {
        throw new Error("Invalid session response");
      }
      const permissions = data.permissions ?? [];
      const localeDefault = data.localeDefault ?? data.locale_default ?? "ko";
      const user = data.user;
      const userId = user?.userId ?? user?.user_id;
      if (!userId) {
        throw new Error("Session missing user id");
      }
      // Fresh app login (no session cookie yet) — not a page refresh with an existing session
      const isFreshAppLogin = getCookieValue(COOKIES.LOGIN_STATUS) !== LOGGED_IN_STATUS;
      if (isFreshAppLogin) {
        // Browser close without logout leaves prior user's IndexedDB + SYNCED_ALL_DATA
        await resetCachedUserData();
      }
      localStorage.setItem(LOCAL_STORAGE.USER_ID, userId);
      localStorage.setItem(LOCAL_STORAGE.PERMISSIONS, JSON.stringify(permissions));
      const existingLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE);
      if (!existingLanguage) {
        localStorage.setItem(LOCAL_STORAGE.LANGUAGE, localeDefault);
      }
      createSessionCookie(COOKIES.LOGIN_STATUS, LOGGED_IN_STATUS);
      if (isFreshAppLogin) {
        // Dashboard shows announcement popup only when this flag is set after login
        localStorage.setItem(LOCAL_STORAGE.JUST_LOGGED_IN, "true");
      }
      updatePermissions(permissions);
      updateLocaleDefault(localeDefault);
      await fetchClientConfig();
    },
    [fetchClientConfig, updateLocaleDefault, updatePermissions],
  );

  useEffect(() => {
    const kc = new Keycloak({
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: KEYCLOAK_FRONTEND_CLIENT_ID,
    });

    let cancelled = false;

    (async () => {
      try {
        const authenticated = await kc.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          silentCheckSsoRedirectUri: buildSilentCheckSsoUrl(),
          checkLoginIframe: false,
          silentCheckSsoFallback: false,
        });
        if (cancelled) return;
        setKeycloakInstance(kc);
        setKeycloak(kc);
        kc.onTokenExpired = async () => {
          try {
            await kc.updateToken(70);
          } catch {
            await clearAuthSession({ clearIndexedDb: true });
            const base = appBasePath();
            const redirectUri = `${window.location.origin}${base}/login`.replace(/([^:]\/)\/+/g, "$1");
            await kc.logout({ redirectUri });
          }
        };
        if (authenticated) {
          try {
            sessionBootstrapInProgressRef.current = true;
            await applySessionPayload(kc);
          } catch (err) {
            console.error("Keycloak session bootstrap failed (user may be unknown to CSC backend)", err);
            await clearKeycloakSessionAfterBootstrapFailure(kc, err);
          } finally {
            sessionBootstrapInProgressRef.current = false;
          }
        }
      } catch (e) {
        console.error("Keycloak init failed", e);
        if (!cancelled) {
          setKeycloakInstance(null);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithKeycloak = useCallback(async (options = {}) => {
    if (!keycloak) return;
    if (keycloak.authenticated) return;
    if (loginRedirectInProgressRef.current) return;
    loginRedirectInProgressRef.current = true;
    const base = appBasePath();
    const targetPath = options.redirectPath || "/dashboard";
    const redirectUri = `${window.location.origin}${base}${targetPath}`.replace(/([^:]\/)\/+/g, "$1");
    const locale = options.locale || (await resolveKeycloakLocale());
    try {
      window.location.assign(buildKeycloakLoginUrl(keycloak, { redirectUri, locale }));
    } finally {
      // In practice browser navigates away. Keep fallback for popup blockers/errors.
      loginRedirectInProgressRef.current = false;
    }
  }, [keycloak]);

  const ensureAuthenticated = useCallback(async (options = {}) => {
    if (!keycloak) {
      return false;
    }

    if (isLogoutInProgress()) {
      return false;
    }

    if (keycloak.authenticated) {
      try {
        await keycloak.updateToken(30);
      } catch {
        await clearAuthSession({ clearIndexedDb: true });
      }
    }

    if (keycloak.authenticated && !hasAppSession() && !sessionBootstrapInProgressRef.current) {
      try {
        sessionBootstrapInProgressRef.current = true;
        await applySessionPayload(keycloak);
      } catch (error) {
        console.error("Failed to bootstrap app session from Keycloak auth", error);
        await clearKeycloakSessionAfterBootstrapFailure(keycloak, error);
      } finally {
        sessionBootstrapInProgressRef.current = false;
      }
    }

    const authenticated = Boolean(keycloak.authenticated && hasAppSession());
    if (!authenticated && options.redirectIfUnauthenticated !== false && !keycloak.authenticated) {
      await loginWithKeycloak({ redirectPath: options.redirectPath });
    }
    return authenticated;
  }, [hasAppSession, keycloak, loginWithKeycloak, applySessionPayload]);

  const value = useMemo(
    () => ({
      enabled: true,
      ready,
      keycloak,
      loginWithKeycloak,
      ensureAuthenticated,
    }),
    [keycloak, loginWithKeycloak, ensureAuthenticated, ready],
  );

  if (!ready) {
    return null;
  }

  return <KeycloakAuthContext.Provider value={value}>{children}</KeycloakAuthContext.Provider>;
}
